import { useCallback, useEffect, useState } from "react";
import { Icon, Loader, Pagination, Segment, Table, Button } from "semantic-ui-react";
import { AppState } from "../../store/configureStore";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { UserRole } from "../../actions/authentificationActions";
import { IVendorContact } from "../../models/vendorContact";
import { getVendorContacts } from "../../api/vendorsContacts";
import { VendorContactRow } from "./VendorContactRow";
import { AddVendorContactsForm } from "./AddVendorContactForm";
import "../ClientContacts/ClientContacts.css";

interface IProps {
    vendorGuid: string | undefined;
}

export const VendorContacts = ({ vendorGuid }: IProps) => {
    const user = useSelector((state: AppState) => state.user);
    const loc = useLocation();
    const [loading, setLoading] = useState(true);
    const [vendorContacts, setVendorContacts] = useState<IVendorContact[]>([]);

    const [page, setPage] = useState(loc.state ? (loc.state as number) : 1);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchVendorContacts = useCallback(async () => {
        setLoading(true);
        const res = await getVendorContacts(page, vendorGuid ?? "");
        setLoading(false);
        setTotalRows(res.totalRows);
        setVendorContacts(res.rows as IVendorContact[]);
    }, [page, vendorGuid]);

    useEffect(() => {
        fetchVendorContacts();
    }, [fetchVendorContacts]);

    const handleFetchVendorContacts = () => {
        setPage(1);
        fetchVendorContacts();
    };

    return (
        <div className="client-contacts-container">
            <div className="container-header">
                <h1>Vendor contacts</h1>
                {user.role === UserRole.Admin && (
                    <Button
                        secondary
                        content="Add new vendor contact"
                        icon="plus"
                        onClick={() => setModalOpen(true)}
                    />
                )}
            </div>
            <div className="client-contacts-display">
                <div className="client-contacts-table">
                    {loading ? (
                        <Loader active inline="centered" size="huge" />
                    ) : (
                        <Segment.Group>
                            <Segment />
                            <Segment className="table-container">
                                <Table basic="very">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Name</Table.HeaderCell>
                                            <Table.HeaderCell>Employer</Table.HeaderCell>
                                            {user.role === UserRole.Admin && (
                                                <Table.HeaderCell>Actions</Table.HeaderCell>
                                            )}
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {vendorContacts.length === 0 ? (
                                            <Table.Row>
                                                <Table.Cell textAlign="center" colSpan="5">
                                                    No matching records found
                                                </Table.Cell>
                                            </Table.Row>
                                        ) : (
                                            vendorContacts.map(x => (
                                                <VendorContactRow
                                                    key={x.guid}
                                                    vendorContact={x}
                                                    handleRemoveVendorContact={
                                                        handleFetchVendorContacts
                                                    }
                                                    page={page.toString()}
                                                />
                                            ))
                                        )}
                                        <Table.Row />
                                    </Table.Body>
                                </Table>
                            </Segment>
                            <Segment>
                                {totalRows > 0 && (
                                    <div className="client-contacts-pagination">
                                        {totalRows > 10 && (
                                            <Pagination
                                                size="small"
                                                activePage={page}
                                                onPageChange={(e, data: any) =>
                                                    setPage(+data.activePage)
                                                }
                                                ellipsisItem={{
                                                    content: <Icon name="ellipsis horizontal" />,
                                                }}
                                                firstItem={{
                                                    content: <Icon name="angle double left" />,
                                                }}
                                                lastItem={{
                                                    content: <Icon name="angle double right" />,
                                                }}
                                                prevItem={{ content: <Icon name="angle left" /> }}
                                                nextItem={{ content: <Icon name="angle right" /> }}
                                                totalPages={Math.ceil(totalRows / 10)}
                                            />
                                        )}
                                    </div>
                                )}
                            </Segment>
                            <AddVendorContactsForm
                                vendorGuid={vendorGuid ? vendorGuid : ""}
                                open={modalOpen}
                                closeModal={() => setModalOpen(false)}
                                handleAddVendorContacts={handleFetchVendorContacts}
                            />
                        </Segment.Group>
                    )}
                </div>
            </div>
        </div>
    );
};
